import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Plus, X, ShoppingCart, Calendar, Send, AlignLeft, User, Check, SquareCheckBig, MessageSquareText, CalendarDays, Flag, Edit2, Save, Upload, MapPin, Clock, Sparkles, DollarSign, Image as ImageIcon, Hammer, Palette, Coffee, Store, Gamepad2, Trophy, Film, Mountain, Home, UtensilsCrossed, GraduationCap, Plane, TreePine, Lightbulb, FileText } from 'lucide-react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { useShopping } from '../../hooks/useShopping';
import { useMessages } from '../../hooks/useMessages';
import { tasksService } from '../../lib/tasks-service';
import AvatarOrInitial from '../Family/AvatarOrInitial';
import { format, addDays } from 'date-fns';
import { supabase } from '../../lib/supabase';
import { useLocation } from 'react-router-dom';

// Enhanced Calendar & Time Picker Component
function EnhancedDateTimePicker({ 
  selectedDate, setSelectedDate, 
  selectedTime, setSelectedTime, 
  setShowDatePicker,
  eventFormData, setEventFormData,
  dateToYYYYMMDD 
}) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [pickerMode, setPickerMode] = useState('single'); // 'single', 'range'
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const [tempTime, setTempTime] = useState('');
  const [timePresets] = useState([
    { label: 'Morning', value: '09:00' },
    { label: 'Lunch', value: '12:00' },
    { label: 'Afternoon', value: '14:00' },
    { label: 'Evening', value: '18:00' },
    { label: 'Night', value: '20:00' }
  ]);
  
  const today = new Date();
  const currentYear = currentMonth.getFullYear();
  const currentMonthIndex = currentMonth.getMonth();
  
  // Initialize with current values
  useEffect(() => {
    setTempStartDate(selectedDate);
    setTempTime(selectedTime);
  }, []);
  
  // Generate calendar days
  const generateCalendarDays = () => {
    const firstDayOfMonth = new Date(currentYear, currentMonthIndex, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonthIndex + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();
    const startingDayOfWeek = firstDayOfMonth.getDay();
    
    const calendarDays = [];
    
    // Previous month's trailing days
    const prevMonth = new Date(currentYear, currentMonthIndex - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      calendarDays.push({
        day: daysInPrevMonth - i,
        date: new Date(currentYear, currentMonthIndex - 1, daysInPrevMonth - i),
        isCurrentMonth: false,
        isPrevMonth: true
      });
    }
    
    // Current month's days
    for (let day = 1; day <= daysInMonth; day++) {
      calendarDays.push({
        day,
        date: new Date(currentYear, currentMonthIndex, day),
        isCurrentMonth: true,
        isPrevMonth: false
      });
    }
    
    // Next month's leading days
    const totalCells = 42; // 6 weeks × 7 days
    const remainingCells = totalCells - calendarDays.length;
    for (let day = 1; day <= remainingCells; day++) {
      calendarDays.push({
        day,
        date: new Date(currentYear, currentMonthIndex + 1, day),
        isCurrentMonth: false,
        isPrevMonth: false
      });
    }
    
    return calendarDays.slice(0, 35); // Show 5 weeks max
  };
  
  const handleDateClick = (date) => {
    const dateStr = dateToYYYYMMDD(date);
    
    if (pickerMode === 'single') {
      setTempStartDate(dateStr);
      setTempEndDate('');
    } else {
      // Range mode
      if (!tempStartDate || (tempStartDate && tempEndDate)) {
        // Start new range
        setTempStartDate(dateStr);
        setTempEndDate('');
      } else if (tempStartDate && !tempEndDate) {
        // Complete range
        if (new Date(dateStr) >= new Date(tempStartDate)) {
          setTempEndDate(dateStr);
        } else {
          // Swap if end date is before start date
          setTempEndDate(tempStartDate);
          setTempStartDate(dateStr);
        }
      }
    }
  };
  
  const isDateInRange = (date) => {
    if (!tempStartDate || !tempEndDate) return false;
    const dateStr = dateToYYYYMMDD(date);
    return dateStr >= tempStartDate && dateStr <= tempEndDate;
  };
  
  const isDateSelected = (date) => {
    const dateStr = dateToYYYYMMDD(date);
    if (pickerMode === 'single') {
      return dateStr === tempStartDate;
    } else {
      return dateStr === tempStartDate || dateStr === tempEndDate;
    }
  };
  
  const applySelection = () => {
    setSelectedDate(tempStartDate);
    setSelectedTime(tempTime);
    
    if (pickerMode === 'range' && tempEndDate) {
      setEventFormData(prev => ({
        ...prev,
        isMultiDay: true,
        startDate: tempStartDate,
        endDate: tempEndDate
      }));
    }
    
    setShowDatePicker(false);
  };
  // Developer note (Sarah): Fixed JSX formatting, accessibility, and improved structure for maintainability and clarity.
  // Added ARIA labels, keyboard navigation, and focus management for accessibility.
  // Ensured all event handlers are properly defined and used.
  // Used React Fragments and removed unnecessary escape characters.

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end"
      onClick={() => setShowDatePicker(false)}
      aria-modal="true"
      role="dialog"
      tabIndex={-1}
      aria-label="Date and time picker dialog"
    >
      <div className="absolute inset-0 bg-black bg-opacity-20" />
      <div
        className="relative w-full bg-white rounded-t-2xl shadow-2xl max-w-lg mx-auto overflow-hidden"
        onClick={e => e.stopPropagation()}
        style={{
          animation: 'slideInFromBottom 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
          maxHeight: '85vh'
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-stone-100 bg-white sticky top-0 z-10">
          <button
            onClick={() => setShowDatePicker(false)}
            className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium transition-colors"
            aria-label="Cancel date selection"
            type="button"
          >
            Cancel
          </button>
          <div className="flex items-center bg-stone-100 rounded-lg p-1" role="group" aria-label="Picker mode">
            <button
              onClick={() => setPickerMode('single')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                pickerMode === 'single'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              aria-pressed={pickerMode === 'single'}
              type="button"
            >
              Single
            </button>
            <button
              onClick={() => setPickerMode('range')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                pickerMode === 'range'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
              aria-pressed={pickerMode === 'range'}
              type="button"
            >
              Range
            </button>
          </div>
          <button
            onClick={applySelection}
            className="px-4 py-2 text-azure-600 hover:text-azure-700 font-medium transition-colors disabled:opacity-50"
            disabled={!tempStartDate}
            aria-label="Apply date selection"
            type="button"
          >
            Done
          </button>
        </div>

        <div className="overflow-y-auto" style={{ maxHeight: 'calc(85vh - 80px)' }}>
          {/* Month Navigation */}
          <div className="flex items-center justify-between p-4 pb-2">
            <button
              onClick={() => setCurrentMonth(new Date(currentYear, currentMonthIndex - 1))}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              aria-label="Previous month"
              type="button"
            >
              <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h3 className="text-lg font-semibold text-slate-900" aria-live="polite">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h3>
            <button
              onClick={() => setCurrentMonth(new Date(currentYear, currentMonthIndex + 1))}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              aria-label="Next month"
              type="button"
            >
              <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Quick Date Buttons */}
          <div className="px-4 pb-3">
            <div className="flex gap-2 flex-wrap" role="group" aria-label="Quick date selection">
              {[
                { label: 'Today', getValue: () => dateToYYYYMMDD(today) },
                {
                  label: 'Tomorrow',
                  getValue: () => {
                    const tomorrow = new Date(today);
                    tomorrow.setDate(today.getDate() + 1);
                    return dateToYYYYMMDD(tomorrow);
                  }
                },
                {
                  label: 'This Weekend',
                  getValue: () => {
                    const saturday = new Date(today);
                    const daysUntilSaturday = 6 - today.getDay();
                    saturday.setDate(today.getDate() + daysUntilSaturday);
                    return dateToYYYYMMDD(saturday);
                  }
                }
              ].map(quick => (
                <button
                  key={quick.label}
                  onClick={() => {
                    const date = quick.getValue();
                    setTempStartDate(date);
                    if (pickerMode === 'range') {
                      setTempEndDate('');
                    }
                  }}
                  className="px-3 py-1.5 text-xs font-medium bg-stone-100 hover:bg-stone-200 text-slate-700 rounded-lg transition-colors"
                  aria-label={`Set date to ${quick.label}`}
                  type="button"
                >
                  {quick.label}
                </button>
              ))}
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="px-4">
            {/* Weekday Headers */}
            <div className="grid grid-cols-7 gap-1 mb-2" aria-hidden="true">
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                <div key={i} className="text-center text-xs font-medium text-stone-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1 mb-4" role="grid" aria-label="Calendar days">
              {generateCalendarDays().map((dayData, i) => {
                const { day, date, isCurrentMonth } = dayData;
                const isToday = date.toDateString() === today.toDateString();
                const isSelected = isDateSelected(date);
                const isInRange = isDateInRange(date);
                const isPast = date < today && !isToday;

                return (
                  <button
                    key={i}
                    onClick={() => !isPast && handleDateClick(date)}
                    disabled={isPast}
                    className={`
                      aspect-square flex items-center justify-center rounded-lg text-sm font-medium
                      transition-all relative disabled:cursor-not-allowed
                      ${
                        isSelected
                          ? 'bg-azure-600 text-white shadow-md scale-105'
                          : isInRange
                            ? 'bg-azure-100 text-azure-900'
                            : isToday
                              ? 'bg-amber-100 text-amber-900 ring-2 ring-amber-300'
                              : isPast
                                ? 'text-stone-300 cursor-not-allowed'
                                : isCurrentMonth
                                  ? 'text-slate-900 hover:bg-stone-100'
                                  : 'text-stone-400 hover:bg-stone-50'
                      }
                    `}
                    aria-label={`Select ${date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`}
                    aria-current={isToday ? "date" : undefined}
                    aria-selected={isSelected}
                    tabIndex={isPast ? -1 : 0}
                    type="button"
                  >
                    {day}
                    {isToday && !isSelected && (
                      <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 w-1.5 h-1.5 bg-amber-600 rounded-full" aria-hidden="true" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Range Selection Summary */}
          {pickerMode === 'range' && (tempStartDate || tempEndDate) && (
            <div className="px-4 py-3 bg-azure-50 border-t border-azure-100" aria-live="polite">
              <div className="text-sm text-azure-900">
                <span className="font-medium">Selected Range:</span>
                <div className="mt-1">
                  {tempStartDate && (
                    <span>
                      {new Date(tempStartDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  )}
                  {tempStartDate && tempEndDate && <span className="mx-2">→</span>}
                  {tempEndDate && (
                    <span>
                      {new Date(tempEndDate).toLocaleDateString('en-US', {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  )}
                  {tempStartDate && !tempEndDate && (
                    <span className="text-azure-600 ml-2">(select end date)</span>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Time Selection */}
          <div className="p-4 border-t border-stone-100">
            <div className="mb-3">
              <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="quickadd-time-input">
                <Clock className="w-4 h-4 inline mr-1" aria-hidden="true" />
                Time (optional)
              </label>

              {/* Time Presets */}
              <div className="flex gap-2 mb-3 flex-wrap" role="group" aria-label="Time presets">
                {timePresets.map(preset => (
                  <button
                    key={preset.value}
                    onClick={() => setTempTime(preset.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                      tempTime === preset.value
                        ? 'bg-azure-600 text-white'
                        : 'bg-stone-100 hover:bg-stone-200 text-slate-700'
                    }`}
                    aria-pressed={tempTime === preset.value}
                    type="button"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>

              {/* Time Input */}
              <div className="flex gap-2">
                <input
                  id="quickadd-time-input"
                  type="time"
                  value={tempTime}
                  onChange={e => setTempTime(e.target.value)}
                  className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500 focus:border-azure-500 text-sm"
                  aria-label="Select time"
                />
                {tempTime && (
                  <button
                    onClick={() => setTempTime('')}
                    className="px-3 py-2 text-stone-500 hover:text-slate-700 hover:bg-stone-100 rounded-lg transition-colors text-sm"
                    aria-label="Clear time"
                    type="button"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function QuickAddGlobal() {
  const location = useLocation();

  // Hide FAB on all /college/* pages
  const isCollegePage = location.pathname.startsWith('/college');
  if (isCollegePage) {
    return null;
  }

  const [isOpen, setIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [selectedType, setSelectedType] = useState(null);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [eventCreationMode, setEventCreationMode] = useState(null); // 'quick' or 'full'
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [calendarMode, setCalendarMode] = useState('single'); // 'single', 'range'
  const [calendarStartDate, setCalendarStartDate] = useState('');
  const [calendarEndDate, setCalendarEndDate] = useState('');
  const [isUrgent, setIsUrgent] = useState(false);
  const [moneyAmount, setMoneyAmount] = useState('');
  const [moneyType, setMoneyType] = useState('deduction'); // 'addition' or 'deduction'
  const [selectedKid, setSelectedKid] = useState(null);
  const [moneyReason, setMoneyReason] = useState('');
  const [eventFormData, setEventFormData] = useState({
    dateTime: '',
    endDateTime: '',
    isMultiDay: false,
    whoInvolved: [],
    privacy: 'only-me',
    outingCategory: 'outdoor-adventures',
    description: '',
    location: '',
    when: '',
    startDate: '',
    endDate: '',
    itemType: 'general-reminder',
    image: null
  });
  const inputRef = useRef(null);
  const moneyAmountRef = useRef(null);
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Helper function to convert date to YYYY-MM-DD without timezone issues
  const dateToYYYYMMDD = (date) => {
    // Format as YYYY-MM-DD using local date values
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Helper function to create a Date object from YYYY-MM-DD string in local timezone
  const localDateFromString = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  // Enhanced natural language date/time parsing
  const parseDateTime = (text) => {
    const today = new Date();
    const lowerText = text.toLowerCase();
    
    // Date range and end date parsing
    let parsedDate = '';
    let parsedEndDate = '';
    let parsedTime = '';
    let isRange = false;
    
    // Check for date ranges first
    const rangePatterns = [
      /from\s+(.+?)\s+to\s+(.+?)(?:\s|$)/i,
      /(.+?)\s+to\s+(.+?)(?:\s|$)/i,
      /(.+?)\s*-\s*(.+?)(?:\s|$)/i,
      /(.+?)\s+through\s+(.+?)(?:\s|$)/i,
      /(.+?)\s+until\s+(.+?)(?:\s|$)/i
    ];
    
    for (const pattern of rangePatterns) {
      const match = lowerText.match(pattern);
      if (match && match[1] && match[2]) {
        const startDate = parseSingleDate(match[1].trim(), today);
        const endDate = parseSingleDate(match[2].trim(), today);
        if (startDate && endDate) {
          parsedDate = startDate;
          parsedEndDate = endDate;
          isRange = true;
          break;
        }
      }
    }
    
    // If no range found, parse single date
    if (!isRange) {
      parsedDate = parseSingleDate(lowerText, today);
    }
    
    // Time parsing - enhanced patterns
    const timePatterns = [
      /(\d{1,2}):(\d{2})\s*([ap])m?/i,  // 3:30pm, 12:45am, 10:15a
      /(\d{1,2})\s*([ap])m?/i,          // 3pm, 12am, 3p
      /(\d{1,2}):(\d{2})/,             // 15:30, 09:15
      /at\s+(\d{1,2}):?(\d{2})?\s*([ap])m?/i,  // at 3pm, at 3:30pm, at 10a
      /(\d{1,2})\s*o'?clock/i,         // 3 oclock, 3 o'clock
      /(morning|afternoon|evening|night|noon|midnight)/i  // general times
    ];
    
    for (const pattern of timePatterns) {
      const timeMatch = lowerText.match(pattern);
      if (timeMatch) {
        if (timeMatch[0].includes('morning')) {
          parsedTime = '09:00';
        } else if (timeMatch[0].includes('afternoon')) {
          parsedTime = '14:00';
        } else if (timeMatch[0].includes('evening')) {
          parsedTime = '18:00';
        } else if (timeMatch[0].includes('night')) {
          parsedTime = '20:00';
        } else if (timeMatch[0].includes('noon')) {
          parsedTime = '12:00';
        } else if (timeMatch[0].includes('midnight')) {
          parsedTime = '00:00';
        } else if (timeMatch[0].includes('oclock')) {
          let hours = parseInt(timeMatch[1]);
          if (hours <= 7) hours += 12; // Assume PM for early hours
          parsedTime = `${hours.toString().padStart(2, '0')}:00`;
        } else {
          let hours = parseInt(timeMatch[1]);
          let minutes = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
          const period = timeMatch[3]?.toLowerCase();
          
          // Handle 'a', 'am', 'p', 'pm'
          if ((period === 'p' || period === 'pm') && hours !== 12) hours += 12;
          if ((period === 'a' || period === 'am') && hours === 12) hours = 0;
          
          // If no AM/PM specified and time is between 1-7, assume PM
          if (!period && hours >= 1 && hours <= 7) {
            hours += 12;
          }
          
          parsedTime = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
        }
        break;
      }
    }
    
    return { 
      date: parsedDate, 
      endDate: parsedEndDate,
      time: parsedTime, 
      isRange: isRange 
    };
  };

  // Helper function to parse individual dates
  const parseSingleDate = (text, today) => {
    const lowerText = text.toLowerCase().trim();
    
    // Relative dates
    if (lowerText.includes('today') || lowerText === 'tdy') {
      return dateToYYYYMMDD(today);
    }
    
    if (lowerText.includes('tomorrow') || lowerText === 'tmr' || lowerText === 'tom') {
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);
      return dateToYYYYMMDD(tomorrow);
    }
    
    if (lowerText.includes('yesterday') || lowerText === 'yday') {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      return dateToYYYYMMDD(yesterday);
    }
    
    // Week-based dates
    if (lowerText.includes('next week')) {
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);
      return dateToYYYYMMDD(nextWeek);
    }
    
    if (lowerText.includes('this week')) {
      return dateToYYYYMMDD(today);
    }
    
    // Specific day patterns
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayAbbrevs = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const todayDayIndex = today.getDay();
    
    // Check full day names and abbreviations
    for (let i = 0; i < dayNames.length; i++) {
      const dayName = dayNames[i];
      const dayAbbrev = dayAbbrevs[i];
      
      if (lowerText.includes(dayName) || lowerText.includes(dayAbbrev)) {
        const isNext = lowerText.includes('next');
        const isThis = lowerText.includes('this');
        
        let daysUntilTarget = i - todayDayIndex;
        
        if (isNext || (!isThis && daysUntilTarget <= 0)) {
          if (daysUntilTarget <= 0) daysUntilTarget += 7;
          if (isNext) daysUntilTarget += 7;
        } else if (daysUntilTarget < 0) {
          daysUntilTarget += 7;
        }
        
        const targetDate = new Date(today);
        targetDate.setDate(today.getDate() + daysUntilTarget);
        return dateToYYYYMMDD(targetDate);
      }
    }
    
    // Numeric date patterns
    const numericPatterns = [
      /(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/,  // MM/DD or MM/DD/YY or MM/DD/YYYY
      /(\d{1,2})-(\d{1,2})(?:-(\d{2,4}))?/,   // MM-DD or MM-DD-YY or MM-DD-YYYY
      /(\d{4})-(\d{1,2})-(\d{1,2})/,          // YYYY-MM-DD
      /(\d{1,2})\.(\d{1,2})(?:\.(\d{2,4}))?/  // MM.DD or MM.DD.YY or MM.DD.YYYY
    ];
    
    for (const pattern of numericPatterns) {
      const match = lowerText.match(pattern);
      if (match) {
        let year, month, day;
        
        if (pattern.source.startsWith('(\\d{4})')) {
          // YYYY-MM-DD format
          year = parseInt(match[1]);
          month = parseInt(match[2]) - 1; // JS months are 0-indexed
          day = parseInt(match[3]);
        } else {
          // MM/DD, MM-DD, MM.DD formats
          month = parseInt(match[1]) - 1; // JS months are 0-indexed
          day = parseInt(match[2]);
          year = match[3] ? parseInt(match[3]) : today.getFullYear();
          
          // Handle 2-digit years
          if (year < 100) {
            year += year < 50 ? 2000 : 1900;
          }
        }
        
        const parsedDate = new Date(year, month, day);
        if (!isNaN(parsedDate.getTime())) {
          return dateToYYYYMMDD(parsedDate);
        }
      }
    }
    
    // Month names
    const monthNames = ['january', 'february', 'march', 'april', 'may', 'june',
                       'july', 'august', 'september', 'october', 'november', 'december'];
    const monthAbbrevs = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                         'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
    
    for (let i = 0; i < monthNames.length; i++) {
      const monthPattern = new RegExp(`(${monthNames[i]}|${monthAbbrevs[i]})\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:\\s+(\\d{4}))?`, 'i');
      const match = lowerText.match(monthPattern);
      if (match) {
        const month = i;
        const day = parseInt(match[2]);
        const year = match[3] ? parseInt(match[3]) : today.getFullYear();
        
        const parsedDate = new Date(year, month, day);
        if (!isNaN(parsedDate.getTime())) {
          return dateToYYYYMMDD(parsedDate);
        }
      }
    }
    
    return '';
  };

  // Auto-parse when input changes and event is selected
  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);
    
    if (selectedType === 'event' && value.trim()) {
      const parsed = parseDateTime(value);
      if (parsed.date) setSelectedDate(parsed.date);
      if (parsed.time) setSelectedTime(parsed.time);
      
      // Handle date ranges
      if (parsed.isRange && parsed.endDate) {
        setEventFormData(prev => ({
          ...prev,
          isMultiDay: true,
          startDate: parsed.date,
          endDate: parsed.endDate
        }));
      }
    }
  };

  // Keyboard height detection for mobile - improved
  useEffect(() => {
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return;

    let initialViewportHeight = window.visualViewport?.height || window.innerHeight;
    
    const updateKeyboardHeight = () => {
      // Use visual viewport API if available (modern browsers)
      if (window.visualViewport) {
        const currentHeight = window.visualViewport.height;
        const heightDiff = window.innerHeight - currentHeight;
        // No offset - exact keyboard height
        setKeyboardHeight(Math.max(0, heightDiff));
      } else {
        // Fallback for older browsers
        const currentHeight = window.innerHeight;
        const heightDiff = initialViewportHeight - currentHeight;
        setKeyboardHeight(Math.max(0, heightDiff));
      }
    };

    // Immediate check when modal opens
    if (isOpen) {
      setTimeout(updateKeyboardHeight, 50);
    }

    // Listen for viewport changes
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateKeyboardHeight);
    } else {
      window.addEventListener('resize', updateKeyboardHeight);
    }

    // Also listen for focusin/focusout events as fallback
    const handleFocusIn = () => {
      setTimeout(updateKeyboardHeight, 200); // Reduced delay
    };
    
    const handleFocusOut = () => {
      // Don't immediately hide on focus out - wait to see if another input gets focus
      setTimeout(() => {
        if (!document.activeElement || document.activeElement.tagName !== 'INPUT') {
          setKeyboardHeight(0);
        }
      }, 100);
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateKeyboardHeight);
      } else {
        window.removeEventListener('resize', updateKeyboardHeight);
      }
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, [isOpen]);

  // Handle image upload
  const handleImageUpload = async (file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be smaller than 5MB');
      return;
    }

    setUploadingImage(true);

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `family-activity-${Date.now()}.${fileExt}`;
      const filePath = `family/${fileName}`;

      const { error } = await supabase.storage
        .from('family-images')
        .upload(filePath, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('family-images')
        .getPublicUrl(filePath);

      setEventFormData(prev => ({ ...prev, image: publicUrl }));
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setUploadingImage(false);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  // Handle file input change
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  // Handle paste event for clipboard images
  const handlePaste = async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) {
          handleImageUpload(file);
        }
        break;
      }
    }
  };

  // We'll define the keyboard shortcut handler after handleInstantSubmit is defined
  
  const { family, currentUser, addActivityToCalendar, addFamilyActivity, addAllowanceTransaction, recordKidSpending } = useFamilyContext();
  const { addItem: addShoppingItem } = useShopping();
  const { sendMessage } = useMessages();

  // Quick event save handler
  const handleQuickEventSave = async () => {
    if (!inputValue.trim()) return;
    
    try {
      const eventData = {
        title: inputValue.trim(),
        description: inputValue.trim(),
        category: 'other',
        created_by: currentUser?.id,
        date: selectedDate || dateToYYYYMMDD(addDays(new Date(), 1)),
        time: selectedTime || null,
        confirmed_for_calendar: true
      };
      
      await addActivityToCalendar(eventData);
      
      // Reset and close
      setInputValue('');
      setSelectedDate('');
      setSelectedTime('');
      setSelectedType(null);
      setIsOpen(false);
    } catch (error) {
      console.error('Error creating quick event:', error);
      alert('Failed to create event. Please try again.');
    }
  };

  // Full event save handler  
  const handleFullEventSave = async () => {
    if (!inputValue.trim() || !selectedEventType) return;
    
    try {
      const baseData = {
        title: inputValue.trim(),
        description: inputValue.trim(),
        category: 'other',
        created_by: currentUser?.id
      };

      switch (selectedEventType) {
        case 'outing-idea': {
          await addFamilyActivity({
            ...baseData,
            category: eventFormData.outingCategory || 'outdoor-adventures',
            location: eventFormData.location || null,
            start_date: eventFormData.startDate || null,
            end_date: eventFormData.isMultiDay ? eventFormData.endDate : null,
            image: eventFormData.image || null,
            color_theme: eventFormData.image ? null : eventFormData.colorTheme || 'teal',
            confirmed_for_calendar: false,
            // Enhanced details
            pricing: eventFormData.priceRange || eventFormData.priceNotes ? {
              range: eventFormData.priceRange || null,
              notes: eventFormData.priceNotes || null
            } : null,
            website: eventFormData.website?.trim() || null,
            phone: eventFormData.phone?.trim() || null,
            estimated_duration: eventFormData.estimatedDuration?.trim() || null,
            age_range: eventFormData.ageRange?.trim() || null,
            special_requirements: eventFormData.specialRequirements?.trim() || null,
            menu_highlights: eventFormData.priceNotes?.trim() || null
          });
          break;
        }

        case 'scheduled-event': {
          const eventDate = new Date(eventFormData.dateTime || selectedDate + 'T09:00');
          const eventData = {
            ...baseData,
            date: format(eventDate, 'yyyy-MM-dd'),
            time: eventFormData.dateTime?.includes(':') && !eventFormData.dateTime.endsWith('00:00') 
              ? format(eventDate, 'HH:mm') 
              : null,
            participants: eventFormData.whoInvolved || [],
            confirmed_for_calendar: true,
            personal_event: false
          };
          
          if (eventFormData.isMultiDay && eventFormData.endDateTime) {
            const endDate = new Date(eventFormData.endDateTime);
            eventData.end_date = format(endDate, 'yyyy-MM-dd');
            eventData.end_time = eventFormData.endDateTime.includes(':') && !eventFormData.endDateTime.endsWith('00:00')
              ? format(endDate, 'HH:mm')
              : null;
          }
          
          await addActivityToCalendar(eventData);
          break;
        }

        case 'personal-event': {
          const personalDate = new Date(eventFormData.dateTime || selectedDate + 'T09:00');
          const personalEventData = {
            ...baseData,
            date: format(personalDate, 'yyyy-MM-dd'),
            time: eventFormData.dateTime?.includes(':') && !eventFormData.dateTime.endsWith('00:00')
              ? format(personalDate, 'HH:mm')
              : null,
            privacy_level: eventFormData.privacy || 'only-me',
            personal_event: true,
            confirmed_for_calendar: true
          };
          
          if (eventFormData.isMultiDay && eventFormData.endDateTime) {
            const personalEndDate = new Date(eventFormData.endDateTime);
            personalEventData.end_date = format(personalEndDate, 'yyyy-MM-dd');
            personalEventData.end_time = eventFormData.endDateTime?.includes(':') && !eventFormData.endDateTime.endsWith('00:00')
              ? format(personalEndDate, 'HH:mm')
              : null;
          }
          
          await addActivityToCalendar(personalEventData);
          break;
        }

        case 'other-item': {
          const otherItemData = {
            ...baseData,
            date: eventFormData.optionalDate || null,
            category: eventFormData.itemType || 'general-reminder',
            type: 'reminder',
            confirmed_for_calendar: true
          };
          
          if (eventFormData.isOtherMultiDay && eventFormData.optionalEndDate) {
            otherItemData.end_date = eventFormData.optionalEndDate;
          }
          
          await addActivityToCalendar(otherItemData);
          break;
        }
      }

      // Reset and close
      setInputValue('');
      setSelectedDate('');
      setSelectedTime('');
      setSelectedType(null);
      setSelectedEventType(null);
      setEventCreationMode(null);
      setEventFormData({
        dateTime: '',
        endDateTime: '',
        isMultiDay: false,
        whoInvolved: [],
        privacy: 'only-me',
        outingCategory: 'outdoor-adventures',
        description: '',
        location: '',
        when: '',
        startDate: '',
        endDate: '',
        itemType: 'general-reminder',
        image: null
      });
      setIsOpen(false);
    } catch (error) {
      console.error('Error creating full event:', error);
      alert('Failed to create event. Please try again.');
    }
  };

  // All family members for selection + self option (same as FrictionlessMessageComposer)
  const allMembers = [
    ...(family?.parents || []),
    ...(family?.kids || [])
  ].filter(member => member.id !== currentUser?.id);

  // Add "Note to Self" option
  const selfOption = {
    id: currentUser?.id,
    name: 'Note to Self',
    avatar: currentUser?.avatar || null,
    role: 'self'
  };

  const allRecipientOptions = [selfOption, ...allMembers];

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Delay focus slightly to ensure modal is fully rendered
      const timer = setTimeout(() => {
        inputRef.current.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Add paste event listener for clipboard images
  useEffect(() => {
    if (isOpen && selectedEventType === 'outing-idea') {
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [isOpen, selectedEventType]);

  // Listen for close events from SmartInputSection
  useEffect(() => {
    const handleCloseQuickAdd = () => {
      setIsOpen(false);
    };

    window.addEventListener('closeQuickAdd', handleCloseQuickAdd);
    return () => window.removeEventListener('closeQuickAdd', handleCloseQuickAdd);
  }, []);


  // Handle instant submission for Shop and To-Do
  const handleInstantSubmit = useCallback(async (type) => {
    if (!inputValue.trim()) return;
    
    setIsProcessing(true);
    
    try {
      if (type === 'shopping') {
        await addShoppingItem({
          name: inputValue.trim(),
          addedBy: currentUser?.id,
          category: 'general'
        });
        setSuccessMessage('Added to shopping list! 🛒');
      } else if (type === 'todo') {
        await tasksService.createTask({
          ownerId: currentUser.id,
          title: inputValue.trim(),
          priority: 'medium',
          taskType: 'manual'
        });
        setSuccessMessage('Task added! ✅');
      }
      
      // Show success message briefly then close
      setTimeout(() => {
        setIsOpen(false);
        setInputValue('');
        setSelectedType(null);
        setSuccessMessage('');
        setIsProcessing(false);
      }, 1500);
      
    } catch (error) {
      console.error(`Error adding ${type}:`, error);
      setSuccessMessage(`Failed to add ${type}. Please try again.`);
      setIsProcessing(false);
      
      // Clear error message after delay
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    }
  }, [addShoppingItem, currentUser?.id, inputValue, setIsOpen, setInputValue, setSelectedType, setSuccessMessage, setIsProcessing]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      if (e.key === 'Escape') {
        setIsOpen(false);
        setSelectedType(null);
        setInputValue('');
        setSelectedRecipients([]);
        setSelectedDate('');
        setSelectedTime('');
        setEventCreationMode(null);
        setSelectedEventType(null);
      } else if (e.key === 'Enter' && !e.shiftKey && inputValue.trim()) {
        // Quick submit for shopping and todo when they're selected
        if (selectedType === 'shopping') {
          e.preventDefault();
          handleInstantSubmit('shopping');
        } else if (selectedType === 'todo') {
          e.preventDefault();
          handleInstantSubmit('todo');
        }
        // For other types, let the form handle it naturally
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedType, inputValue, handleInstantSubmit]);

  // Handle button selection with instant submit for Shop/To-Do
  const handleTypeSelection = (type) => {
    if (!inputValue.trim()) {
      setSelectedType(type);
      
      // Auto-focus money amount input if Money is selected
      if (type === 'money') {
        setTimeout(() => {
          if (moneyAmountRef.current) {
            moneyAmountRef.current.focus();
          }
        }, 100);
      }
      
      return;
    }
    
    // Instant submit for Shop and To-Do
    if (type === 'shopping' || type === 'todo') {
      handleInstantSubmit(type);
    } else {
      // Multi-step flow for Event, Remind, and Money
      setSelectedType(type);
      
      // Auto-focus money amount input if Money is selected
      if (type === 'money') {
        setTimeout(() => {
          if (moneyAmountRef.current) {
            moneyAmountRef.current.focus();
          }
        }, 100);
      }
    }
  };

  // Show on all devices for universal quick add access

  return (
    <>
      <style jsx="true">{`
        @keyframes slideInFromBottom {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideOutToBottom {
          from {
            opacity: 1;
            transform: translateY(0);
          }
          to {
            opacity: 0;
            transform: translateY(100%);
          }
        }
        
        .keyboard-modal {
          transition: bottom 0.3s cubic-bezier(0.16, 1, 0.3, 1);
        }
        
      `}</style>

      {/* Floating Action Button - Always visible, changes icon when open */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed w-14 h-14 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110 z-[70] flex items-center justify-center
                   right-4 bottom-4
                   sm:right-6 sm:bottom-6
                   lg:right-8 lg:bottom-8"
        title={isOpen ? "Close" : "Quick Add"}
        style={{ 
          backgroundColor: isOpen ? '#6b7280' : '#7e93ef',
          boxShadow: isOpen ? '0 4px 16px rgba(107, 114, 128, 0.3)' : '0 4px 16px rgba(126, 147, 239, 0.4)',
          minWidth: '56px', 
          minHeight: '56px',
          // Ensure it's above mobile nav on mobile devices
          bottom: window.innerWidth <= 768 ? 'calc(64px + env(safe-area-inset-bottom) + 1rem)' : undefined
        }}
      >
        {isOpen ? (
          <X className="w-6 h-6" />
        ) : (
          <Plus className="w-6 h-6" />
        )}
      </button>

      {/* Quick Add Modal - Keyboard-aware positioning */}
      {isOpen && (
        <>
          {/* Light backdrop for desktop only */}
          <div 
            className="fixed inset-0 z-40 hidden sm:block bg-black bg-opacity-20"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setIsOpen(false);
                setSelectedType(null);
                setInputValue('');
                setSelectedRecipients([]);
              }
            }}
          />
          
          {/* Modal Content - Simple full-space approach */}
          <div 
            className={`fixed z-50 bg-white flex flex-col overflow-hidden
                       ${window.innerWidth <= 768 ? 'inset-0 pt-safe' : 'inset-auto left-4 right-4 bottom-4 max-w-2xl mx-auto rounded-2xl border shadow-2xl'}`}
            style={{
              // Mobile: Full viewport or space above keyboard with safe area
              top: window.innerWidth <= 768 ? 'env(safe-area-inset-top, 0px)' : 'auto',
              paddingTop: window.innerWidth <= 768 ? 'env(safe-area-inset-top, 20px)' : '0',
              bottom: window.innerWidth <= 768 
                ? keyboardHeight > 0 ? `${keyboardHeight}px` : '0'
                : '20px',
              height: window.innerWidth <= 768
                ? keyboardHeight > 0 
                  ? `calc(100vh - ${keyboardHeight}px - env(safe-area-inset-top, 0px))` 
                  : `calc(100vh - env(safe-area-inset-top, 0px))`
                : 'auto',
              maxHeight: window.innerWidth > 768 ? '80vh' : undefined,
              animation: window.innerWidth <= 768 ? 'none' : 'slideInFromBottom 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards'
            }}
          >
            {/* Minimal Header - No close button needed */}
            <div className="flex items-center px-4 py-3 border-b border-stone-100 flex-shrink-0 bg-stone-50">
              <div className="flex items-center gap-2">
                <div className="w-1 h-6 bg-azure-500 rounded-full"></div>
                <span className="text-sm font-medium text-slate-700">Quick Add</span>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="p-4 space-y-4 pb-6">
                {/* Input Field */}
                <div>
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={handleInputChange}
                    placeholder="What would you like to add?"
                    className={`w-full px-4 py-3 text-base border rounded-xl focus:outline-none transition-all duration-200 ${
                      selectedType === 'money' 
                        ? 'border-stone-200 bg-stone-100 text-stone-400 cursor-not-allowed' 
                        : 'border-stone-300 bg-white focus:border-azure-500 focus:ring-2 focus:ring-azure-200'
                    }`}
                    autoFocus={selectedType !== 'money'}
                    disabled={selectedType === 'money'}
                  />
                </div>

                {/* Action Type Buttons */}
                <div className="grid grid-cols-5 gap-2">
                  {[
                    { 
                      key: 'shopping', 
                      icon: ShoppingCart, 
                      label: 'Shop',
                      baseColor: 'coral', // coral
                      selectedBg: 'bg-coral-50',
                      selectedBorder: 'border-coral-500',
                      selectedText: 'text-coral-500'
                    },
                    { 
                      key: 'event', 
                      icon: Calendar, 
                      label: 'Event',
                      baseColor: 'gold', // gold
                      selectedBg: 'bg-gold-50', 
                      selectedBorder: 'border-gold-500',
                      selectedText: 'text-gold-500'
                    },
                    { 
                      key: 'message', 
                      icon: MessageSquareText, 
                      label: 'Remind',
                      baseColor: 'magenta', // magenta
                      selectedBg: 'bg-magenta-50',
                      selectedBorder: 'border-magenta-500', 
                      selectedText: 'text-magenta-500'
                    },
                    { 
                      key: 'todo', 
                      icon: SquareCheckBig, 
                      label: 'To-Do',
                      baseColor: 'azure',
                      selectedBg: 'bg-azure-50',
                      selectedBorder: 'border-azure-500',
                      selectedText: 'text-azure-500'
                    },
                    { 
                      key: 'money', 
                      icon: DollarSign, 
                      label: 'Money',
                      baseColor: 'emerald',
                      selectedBg: 'bg-emerald-50',
                      selectedBorder: 'border-emerald-500',
                      selectedText: 'text-emerald-500'
                    }
                  ].map((type) => {
                    const Icon = type.icon;
                    const isSelected = selectedType === type.key;
                    const isOtherSelected = selectedType && selectedType !== type.key;
                    
                    return (
                      <button
                        key={type.key}
                        onClick={() => handleTypeSelection(type.key)}
                        disabled={isProcessing}
                        className={`flex flex-col items-center gap-1.5 p-2.5 rounded-lg border transition-all duration-200 ${
                          isSelected
                            ? `${type.selectedBg} ${type.selectedBorder} ${type.selectedText} shadow-sm ring-2 ring-opacity-20`
                            : isOtherSelected
                              ? 'bg-stone-50 border-stone-200 text-stone-400 opacity-50'
                              : 'bg-white border-stone-300 text-slate-600 hover:border-stone-400 hover:bg-stone-50 hover:shadow-sm'
                        } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-xs font-medium leading-tight">{type.label}</span>
                      </button>
                    );
                  })}
                </div>

                {/* Success Message */}
                {successMessage && (
                  <div className="flex items-center justify-center gap-2 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <span className="text-emerald-800 font-medium">{successMessage}</span>
                    {isProcessing && (
                      <div className="w-4 h-4 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                    )}
                  </div>
                )}

                {/* Show type-specific content only for Event and Remind */}
                {selectedType === 'message' && !successMessage && (
                  <div className="space-y-4">
                    {/* Send to Section */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-3">Send to:</label>
                      <div className="flex gap-3 mb-4">
                        {allRecipientOptions.map((member) => {
                          const isSelected = selectedRecipients.includes(member.id);
                          return (
                            <button
                              key={member.id}
                              onClick={() => {
                                setSelectedRecipients(prev => 
                                  prev.includes(member.id) 
                                    ? prev.filter(id => id !== member.id)
                                    : [...prev, member.id]
                                );
                              }}
                              className={`relative w-12 h-12 rounded-full transition-all duration-200 ${
                                isSelected
                                  ? 'ring-2 ring-red-400 ring-opacity-50 shadow-lg'
                                  : 'ring-2 ring-transparent hover:ring-stone-300'
                              }`}
                            >
                              {member.role === 'self' ? (
                                <AvatarOrInitial
                                  avatar={currentUser?.avatar}
                                  name={currentUser?.name || 'You'}
                                  size="xl"
                                />
                              ) : (
                                <AvatarOrInitial
                                  avatar={member.avatar}
                                  name={member.name}
                                  size="xl"
                                />
                              )}
                              {isSelected && (
                                <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-red-400 rounded-full flex items-center justify-center border-2 border-white">
                                  <Check className="w-3 h-3 text-white" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    
                    {/* Selected Recipients Display with Action Buttons */}
                    {selectedRecipients.length > 0 && (
                      <div className="flex items-center gap-2 p-3 bg-magenta-50 rounded-xl border border-magenta-100">
                        <span className="text-sm font-medium text-slate-700">To:</span>
                        <span className="text-sm text-slate-900 flex-1">
                          {selectedRecipients.map(id => {
                            const member = allRecipientOptions.find(m => m.id === id);
                            return member?.name;
                          }).join(', ')}
                        </span>
                        
                        {/* Action buttons */}
                        <div className="flex items-center gap-2">
                          {/* Calendar Icon */}
                          <button 
                            onClick={() => setShowDatePicker(!showDatePicker)}
                            className={`p-2 rounded-lg transition-colors ${
                              selectedDate 
                                ? 'bg-azure-100 text-azure-600 hover:bg-azure-200' 
                                : 'text-stone-400 hover:text-slate-600 hover:bg-stone-100'
                            }`}
                            title={selectedDate ? `Due: ${new Date(selectedDate).toLocaleDateString()}` : 'Set due date'}
                          >
                            <CalendarDays className="w-4 h-4" />
                          </button>
                          
                          {/* Urgency Flag */}
                          <button 
                            onClick={() => setIsUrgent(!isUrgent)}
                            className={`p-2 rounded-lg transition-colors ${
                              isUrgent 
                                ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                                : 'text-stone-400 hover:text-slate-600 hover:bg-stone-100'
                            }`}
                            title={isUrgent ? 'Urgent' : 'Mark as urgent'}
                          >
                            <Flag className="w-4 h-4" />
                          </button>
                          
                          {/* Send Button */}
                          <button 
                            onClick={async () => {
                              if (!inputValue.trim() || selectedRecipients.length === 0) return;
                              
                              setIsProcessing(true);
                              try {
                                // Determine message type based on current user role  
                                const messageType = currentUser?.isParent ? 'reminder' : 'to_parent';
                                
                                await sendMessage({
                                  content: inputValue.trim(),
                                  recipientIds: selectedRecipients,
                                  priority: isUrgent ? 'urgent' : 'normal',
                                  type: messageType,
                                  dueDate: selectedDate || null,
                                  subject: isUrgent ? '🔥 Urgent Reminder' : 'Family Reminder'
                                });
                                
                                // Show success message
                                setSuccessMessage('Reminder sent! 📨');
                                
                                // Reset form after delay
                                setTimeout(() => {
                                  setIsOpen(false);
                                  setSelectedType(null);
                                  setInputValue('');
                                  setSelectedRecipients([]);
                                  setSelectedDate('');
                                  setIsUrgent(false);
                                  setSuccessMessage('');
                                  setIsProcessing(false);
                                }, 1500);
                                
                              } catch (error) {
                                console.error('Error sending reminder:', error);
                                setSuccessMessage('Failed to send reminder. Please try again.');
                                setIsProcessing(false);
                                
                                // Clear error message after delay
                                setTimeout(() => {
                                  setSuccessMessage('');
                                }, 3000);
                              }
                            }}
                            disabled={!inputValue.trim() || selectedRecipients.length === 0 || isProcessing}
                            className="ml-2 p-2 bg-magenta-500 text-white rounded-lg hover:bg-magenta-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {isProcessing ? (
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Money/Allowance Management */}
                {selectedType === 'money' && !successMessage && (
                  <div className="space-y-4">
                    {/* Amount Input */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Amount</label>
                      <div className="relative">
                        <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
                        <input
                          ref={moneyAmountRef}
                          type="number"
                          step="0.01"
                          min="0"
                          value={moneyAmount}
                          onChange={(e) => setMoneyAmount(e.target.value)}
                          placeholder="0.00"
                          className="w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200 text-lg font-medium"
                        />
                      </div>
                    </div>

                    {/* For Parents: Kid Selection */}
                    {currentUser?.isParent && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">For which kid?</label>
                        <div className="flex gap-3">
                          {family?.kids?.map((kid) => {
                            const isSelected = selectedKid === kid.id;
                            return (
                              <button
                                key={kid.id}
                                onClick={() => setSelectedKid(kid.id)}
                                className={`relative w-12 h-12 rounded-full transition-all duration-200 ${
                                  isSelected
                                    ? 'ring-2 ring-emerald-400 ring-opacity-50 shadow-lg'
                                    : 'ring-2 ring-transparent hover:ring-stone-300'
                                }`}
                              >
                                <AvatarOrInitial
                                  avatar={kid.avatar}
                                  name={kid.name}
                                  size="xl"
                                />
                                {isSelected && (
                                  <div className="absolute -bottom-0.5 -right-0.5 w-5 h-5 bg-emerald-400 rounded-full flex items-center justify-center border-2 border-white">
                                    <Check className="w-3 h-3 text-white" />
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Transaction Type (For Parents) */}
                    {currentUser?.isParent && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">Transaction Type</label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => setMoneyType('addition')}
                            className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                              moneyType === 'addition'
                                ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                                : 'bg-white border-stone-200 text-slate-600 hover:border-stone-300'
                            }`}
                          >
                            <Plus className="w-5 h-5" />
                            <span className="font-medium">Add Money</span>
                          </button>
                          <button
                            onClick={() => setMoneyType('deduction')}
                            className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                              moneyType === 'deduction'
                                ? 'bg-red-50 border-red-500 text-red-700'
                                : 'bg-white border-stone-200 text-slate-600 hover:border-stone-300'
                            }`}
                          >
                            <X className="w-5 h-5" />
                            <span className="font-medium">Deduct Money</span>
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Reason */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {currentUser?.isParent ? 'Reason (optional)' : 'What did you spend it on?'}
                      </label>
                      <input
                        type="text"
                        value={moneyReason}
                        onChange={(e) => setMoneyReason(e.target.value)}
                        placeholder={currentUser?.isParent ? "Add a note..." : "e.g., candy, toy, game..."}
                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-200"
                      />
                    </div>

                    {/* Preview & Submit */}
                    {moneyAmount && (currentUser?.isParent ? selectedKid : true) && (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <span className="text-sm font-medium text-slate-900">
                              {currentUser?.isParent 
                                ? `${moneyType === 'addition' ? 'Add' : 'Deduct'} $${moneyAmount} ${moneyType === 'addition' ? 'to' : 'from'} ${family?.kids?.find(k => k.id === selectedKid)?.name}`
                                : `Deduct $${moneyAmount} from my allowance`
                              }
                            </span>
                            {moneyReason && (
                              <div className="text-xs text-slate-600 mt-1">
                                For: {moneyReason}
                              </div>
                            )}
                          </div>
                          <button 
                            onClick={async () => {
                              setIsProcessing(true);
                              
                              try {
                                const amount = parseFloat(moneyAmount);
                                const targetKidId = currentUser?.isParent ? selectedKid : currentUser?.id;
                                const reason = moneyReason || 'No reason provided';
                                
                                if (currentUser?.isParent) {
                                  // Parent adding/deducting money
                                  await addAllowanceTransaction(
                                    targetKidId,
                                    amount,
                                    moneyType, // 'addition' or 'deduction'
                                    reason
                                  );
                                } else {
                                  // Kid spending their own money (only deduction allowed)
                                  await recordKidSpending(
                                    currentUser.id,
                                    amount,
                                    reason
                                  );
                                }
                                
                                // Show success message
                                setSuccessMessage(`${currentUser?.isParent ? 'Transaction' : 'Spending'} recorded! 💰`);
                                
                                // Reset form after delay
                                setTimeout(() => {
                                  setIsOpen(false);
                                  setSelectedType(null);
                                  setMoneyAmount('');
                                  setMoneyReason('');
                                  setSelectedKid(null);
                                  setSuccessMessage('');
                                  setIsProcessing(false);
                                }, 1500);
                                
                              } catch (error) {
                                console.error('Error processing money transaction:', error);
                                setSuccessMessage('Failed to record transaction. Please try again.');
                                setIsProcessing(false);
                                
                                // Clear error message after delay
                                setTimeout(() => {
                                  setSuccessMessage('');
                                }, 3000);
                              }
                            }}
                            className="p-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors"
                          >
                            <DollarSign className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Event Creation - Streamlined Flow */}
                {selectedType === 'event' && !successMessage && (
                  <div className="space-y-4">
                    {!eventCreationMode ? (
                      /* Main Event Interface */
                      <div className="space-y-4">
                        {(() => {
                          const detected = parseDateTime(inputValue);
                          const hasDetection = detected?.date || detected?.time;
                          
                          return (
                            <>
                              {/* Auto-detected Quick Add */}
                              {hasDetection ? (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3 flex-1">
                                      <CalendarDays className="w-5 h-5 text-amber-600" />
                                      <div className="flex-1">
                                        {detected.isRange && detected.endDate ? (
                                          <div className="space-y-1">
                                            <div className="font-medium text-slate-900 text-sm">
                                              {detected.date ? 
                                                localDateFromString(detected.date)?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 
                                                'Start TBD'
                                              }
                                              <span className="text-stone-500 mx-2">→</span>
                                              {detected.endDate ? 
                                                localDateFromString(detected.endDate)?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 
                                                'End TBD'
                                              }
                                            </div>
                                            <div className="text-xs text-amber-700 bg-amber-100 px-2 py-1 rounded-full inline-block">
                                              Multi-day event
                                            </div>
                                          </div>
                                        ) : (
                                          <span className="font-medium text-slate-900 text-sm">
                                            {detected.date ? 
                                              localDateFromString(detected.date)?.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 
                                              'Date TBD'
                                            }
                                          </span>
                                        )}
                                        {detected.time && (
                                          <span className="text-slate-600 text-sm ml-2">
                                            at {new Date(`2000-01-01T${detected.time}`).toLocaleTimeString('en-US', { 
                                              hour: 'numeric', 
                                              minute: '2-digit',
                                              hour12: true 
                                            })}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-1">
                                      <button 
                                        onClick={() => {
                                          if (detected.date) setSelectedDate(detected.date);
                                          if (detected.time) setSelectedTime(detected.time);
                                          setShowDatePicker(true);
                                        }}
                                        className="p-2 text-amber-600 hover:bg-amber-100 rounded-lg transition-colors"
                                        title="Edit date/time"
                                      >
                                        <Edit2 className="w-4 h-4" />
                                      </button>
                                      <button 
                                        onClick={async () => {
                                          if (detected.date) setSelectedDate(detected.date);
                                          if (detected.time) setSelectedTime(detected.time);
                                          
                                          // Wait for state to update before saving
                                          await new Promise(resolve => setTimeout(resolve, 0));
                                          
                                          const eventData = {
                                            title: inputValue.trim(),
                                            description: inputValue.trim(),
                                            category: 'other',
                                            created_by: currentUser?.id,
                                            date: detected.date || dateToYYYYMMDD(addDays(new Date(), 1)),
                                            time: detected.time || null,
                                            confirmed_for_calendar: true
                                          };
                                          
                                          try {
                                            await addActivityToCalendar(eventData);
                                            
                                            // Reset and close
                                            setInputValue('');
                                            setSelectedDate('');
                                            setSelectedTime('');
                                            setSelectedType(null);
                                            setIsOpen(false);
                                          } catch (error) {
                                            console.error('Error creating quick event:', error);
                                            alert('Failed to create event. Please try again.');
                                          }
                                        }}
                                        className="p-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
                                        title="Save to calendar"
                                      >
                                        <Save className="w-4 h-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                /* No Detection - Show Basic Date/Time Picker */
                                <div className="p-4 bg-stone-50 border border-stone-200 rounded-xl">
                                  <div className="space-y-3 mb-4">
                                    <div>
                                      <label className="block text-xs font-medium text-slate-600 mb-1">Date</label>
                                      <input
                                        type="date"
                                        value={selectedDate}
                                        onChange={(e) => setSelectedDate(e.target.value)}
                                        className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-azure-400 focus:ring-2 focus:ring-azure-200 text-sm"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-xs font-medium text-slate-600 mb-1">Time (optional)</label>
                                      <div className="flex gap-2">
                                        <input
                                          type="time"
                                          value={selectedTime}
                                          onChange={(e) => setSelectedTime(e.target.value)}
                                          className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:border-azure-400 focus:ring-2 focus:ring-azure-200 text-sm"
                                        />
                                        {selectedTime && (
                                          <button 
                                            onClick={() => setSelectedTime('')}
                                            className="px-3 py-2 text-stone-500 hover:text-slate-700 hover:bg-stone-100 rounded-lg transition-colors text-sm"
                                          >
                                            Clear
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <button 
                                    onClick={handleQuickEventSave}
                                    disabled={!selectedDate}
                                    className="w-full px-4 py-2 bg-azure-500 text-white rounded-lg hover:bg-azure-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    Save to Calendar
                                  </button>
                                </div>
                              )}

                              {/* Full Event Creation Option */}
                              <div className="text-center">
                                <button
                                  onClick={() => setEventCreationMode('full')}
                                  className="text-sm text-azure-600 hover:text-azure-700 font-medium"
                                >
                                  Need more options? Create full event →
                                </button>
                              </div>

                              {/* Quick Event Type Buttons */}
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  { 
                                    key: 'outing-idea', 
                                    title: 'Outing Idea', 
                                    icon: Lightbulb,
                                    color: 'emerald'
                                  },
                                  { 
                                    key: 'scheduled-event', 
                                    title: 'Scheduled', 
                                    icon: Calendar,
                                    color: 'azure'
                                  },
                                  { 
                                    key: 'personal-event', 
                                    title: 'Personal', 
                                    icon: User,
                                    color: 'violet'
                                  },
                                  { 
                                    key: 'other-item', 
                                    title: 'Other', 
                                    icon: FileText,
                                    color: 'orange'
                                  }
                                ].map((eventType) => {
                                  const Icon = eventType.icon;
                                  return (
                                    <button
                                      key={eventType.key}
                                      onClick={() => {
                                        setSelectedEventType(eventType.key);
                                        setEventCreationMode('full');
                                      }}
                                      className="flex items-center gap-2 p-2.5 bg-white border border-stone-200 rounded-lg hover:border-stone-300 hover:bg-stone-50 transition-colors text-left"
                                    >
                                      <Icon className="w-5 h-5 text-slate-600" />
                                      <span className="text-sm font-medium text-slate-700">{eventType.title}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </>
                          );
                        })()}
                      </div>
                    ) : eventCreationMode === 'edit' ? (
                      /* Edit Mode */
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-4">
                          <button
                            onClick={() => setEventCreationMode(null)}
                            className="p-1 hover:bg-stone-100 rounded"
                          >
                            <X className="w-4 h-4 text-stone-400" />
                          </button>
                          <h4 className="font-medium text-slate-900">Edit Event Details</h4>
                        </div>

                        <div className="flex gap-3">
                          <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="flex-1 px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:border-azure-400"
                          />
                          <input
                            type="time"
                            value={selectedTime}
                            onChange={(e) => setSelectedTime(e.target.value)}
                            className="px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:border-azure-400"
                          />
                        </div>

                        <button 
                          onClick={handleQuickEventSave}
                          className="w-full px-4 py-2 bg-azure-500 text-white rounded-lg hover:bg-azure-600 transition-colors"
                        >
                          Save to Calendar
                        </button>
                      </div>
                    ) : (
                      /* Full Event Creation Forms */
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-4">
                          <button
                            onClick={() => {
                              if (selectedEventType) {
                                setSelectedEventType(null);
                              } else {
                                setEventCreationMode(null);
                              }
                            }}
                            className="p-1 hover:bg-stone-100 rounded"
                          >
                            <X className="w-4 h-4 text-stone-400" />
                          </button>
                          <h4 className="font-medium text-slate-900">
                            {selectedEventType && {
                              'outing-idea': 'Family Outing Idea',
                              'scheduled-event': 'Scheduled Event',
                              'personal-event': 'Personal Event',
                              'other-item': 'Other Item'
                            }[selectedEventType]}
                          </h4>
                        </div>

                        {/* Family Outing Idea Form */}
                        {selectedEventType === 'outing-idea' && (
                          <div className="space-y-3">
                            {/* Description */}
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-1">Description</label>
                              <textarea
                                value={eventFormData.description}
                                onChange={(e) => setEventFormData(prev => ({ ...prev, description: e.target.value }))}
                                className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:border-azure-400 text-sm resize-none"
                                rows={2}
                                placeholder="Tell us more about this idea..."
                              />
                            </div>

                            {/* Category Selection */}
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-3">Category</label>
                              <div className="grid grid-cols-2 gap-2">
                                {[
                                  { value: 'woodworking', icon: Hammer, label: 'Woodworking' },
                                  { value: 'art-craft', icon: Palette, label: 'Art & Craft' },
                                  { value: 'casual-bites', icon: Coffee, label: 'Casual Bites' },
                                  { value: 'specialty-shops', icon: Store, label: 'Specialty Shops' },
                                  { value: 'games-interactive', icon: Gamepad2, label: 'Games & Interactive' },
                                  { value: 'sports-shows', icon: Trophy, label: 'Sports & Shows' },
                                  { value: 'movies-music', icon: Film, label: 'Movies & Music' },
                                  { value: 'outdoor-adventures', icon: Mountain, label: 'Outdoor Adventures' },
                                  { value: 'at-home-fun', icon: Home, label: 'At-Home Fun' },
                                  { value: 'dining-out', icon: UtensilsCrossed, label: 'Dining Out' },
                                  { value: 'educational', icon: GraduationCap, label: 'Educational' },
                                  { value: 'travel-trips', icon: Plane, label: 'Travel & Trips' },
                                  { value: 'festivals-events', icon: TreePine, label: 'Festivals & Events' },
                                  { value: 'other', icon: Sparkles, label: 'Other' }
                                ].map(cat => {
                                  const Icon = cat.icon;
                                  return (
                                    <button
                                      key={cat.value}
                                      onClick={() => setEventFormData(prev => ({ ...prev, outingCategory: cat.value }))}
                                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                                        eventFormData.outingCategory === cat.value
                                          ? 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                          : 'bg-white text-slate-700 border-stone-200 hover:bg-stone-50 hover:border-stone-300'
                                      }`}
                                    >
                                      <Icon className="w-4 h-4" />
                                      <span>{cat.label}</span>
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Color Theme */}
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-3">Color Theme</label>
                              <div className="flex items-center gap-3">
                                {[
                                  { value: 'magenta', color: '#e91e63', label: 'Magenta' },
                                  { value: 'coral', color: '#ff6b6b', label: 'Coral' },
                                  { value: 'deep-orange', color: '#ff7043', label: 'Deep Orange' },
                                  { value: 'gold', color: '#ffd54f', label: 'Gold' },
                                  { value: 'teal', color: '#26a69a', label: 'Teal' },
                                  { value: 'azure', color: '#42a5f5', label: 'azure' },
                                  { value: 'indigo', color: '#7e57c2', label: 'Indigo' },
                                  { value: 'violet', color: '#ab47bc', label: 'Violet' }
                                ].map(theme => (
                                  <button
                                    key={theme.value}
                                    onClick={() => setEventFormData(prev => ({ ...prev, colorTheme: theme.value }))}
                                    className={`w-8 h-8 rounded-full transition-all ${
                                      eventFormData.colorTheme === theme.value
                                        ? 'ring-2 ring-offset-2 ring-stone-400 scale-110'
                                        : 'hover:scale-105'
                                    }`}
                                    style={{ backgroundColor: theme.color }}
                                    title={theme.label}
                                  />
                                ))}
                              </div>
                            </div>

                            {/* Location */}
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-3">Location (optional)</label>
                              <div className="flex flex-wrap gap-2">
                                {['Raleigh', 'Cary', 'Apex', 'Holly Springs', 'Fuquay Varina', 'Other'].map(loc => (
                                  <button
                                    key={loc}
                                    onClick={() => setEventFormData(prev => ({ ...prev, location: loc }))}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                                      eventFormData.location === loc
                                        ? 'bg-azure-50 text-azure-700 border-azure-300'
                                        : 'bg-white text-slate-700 border-stone-200 hover:bg-stone-50 hover:border-stone-300'
                                    }`}
                                  >
                                    {loc}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Timing */}
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-3">When would be good?</label>
                              <div className="flex flex-wrap gap-2">
                                {['Next weekend', 'Someday', 'Summer', 'Pick dates'].map(when => (
                                  <button
                                    key={when}
                                    onClick={() => {
                                      if (when === 'Pick dates') {
                                        setShowDatePicker(true);
                                      } else {
                                        setEventFormData(prev => ({ ...prev, when }));
                                      }
                                    }}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors border ${
                                      eventFormData.when === when
                                        ? 'bg-violet-50 text-violet-700 border-violet-300'
                                        : 'bg-white text-slate-700 border-stone-200 hover:bg-stone-50 hover:border-stone-300'
                                    }`}
                                  >
                                    {when}
                                  </button>
                                ))}
                              </div>
                            </div>

                            {/* Image Upload Area */}
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-2">
                                Image (optional)
                              </label>
                              {eventFormData.image ? (
                                <div className="relative">
                                  <img 
                                    src={eventFormData.image} 
                                    alt="Activity" 
                                    className="w-full h-32 object-cover rounded-lg"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => setEventFormData(prev => ({ ...prev, image: null }))}
                                    className="absolute top-2 right-2 p-1 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              ) : (
                                <div 
                                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                                    dragOver 
                                      ? 'border-azure-400 bg-azure-50' 
                                      : uploadingImage 
                                        ? 'border-stone-300 bg-stone-50' 
                                        : 'border-stone-300 hover:border-stone-400'
                                  }`}
                                  onDragOver={handleDragOver}
                                  onDragLeave={handleDragLeave}
                                  onDrop={handleDrop}
                                  onClick={() => !uploadingImage && fileInputRef.current?.click()}
                                >
                                  <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileInputChange}
                                    className="hidden"
                                  />
                                  {uploadingImage ? (
                                    <div className="space-y-2">
                                      <div className="w-6 h-6 border-2 border-azure-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                                      <p className="text-xs text-stone-500">Uploading...</p>
                                    </div>
                                  ) : (
                                    <div className="space-y-2">
                                      <ImageIcon className="w-6 h-6 text-stone-400 mx-auto" />
                                      <p className="text-xs text-stone-500">
                                        Drag & drop, paste, or <span className="text-azure-600 font-medium">browse</span> for an image
                                      </p>
                                      <p className="text-xs text-stone-400">PNG, JPG up to 5MB</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* Enhanced Fields */}
                            <div className="space-y-3">
                              <div className="grid grid-cols-2 gap-3">
                                {/* Price Range */}
                                <div>
                                  <label className="block text-xs font-medium text-slate-600 mb-1">Price Range</label>
                                  <input
                                    type="text"
                                    value={eventFormData.priceRange}
                                    onChange={(e) => setEventFormData(prev => ({ ...prev, priceRange: e.target.value }))}
                                    placeholder="e.g., $15-25 per person"
                                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-azure-400"
                                  />
                                </div>
                                
                                {/* Duration */}
                                <div>
                                  <label className="block text-xs font-medium text-slate-600 mb-1">Duration</label>
                                  <input
                                    type="text"
                                    value={eventFormData.estimatedDuration}
                                    onChange={(e) => setEventFormData(prev => ({ ...prev, estimatedDuration: e.target.value }))}
                                    placeholder="e.g., 2 hours"
                                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-azure-400"
                                  />
                                </div>
                                
                                {/* Website */}
                                <div>
                                  <label className="block text-xs font-medium text-slate-600 mb-1">Website</label>
                                  <input
                                    type="url"
                                    value={eventFormData.website}
                                    onChange={(e) => setEventFormData(prev => ({ ...prev, website: e.target.value }))}
                                    placeholder="https://..."
                                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-azure-400"
                                  />
                                </div>
                                
                                {/* Phone */}
                                <div>
                                  <label className="block text-xs font-medium text-slate-600 mb-1">Phone</label>
                                  <input
                                    type="tel"
                                    value={eventFormData.phone}
                                    onChange={(e) => setEventFormData(prev => ({ ...prev, phone: e.target.value }))}
                                    placeholder="(555) 123-4567"
                                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-azure-400"
                                  />
                                </div>
                                
                                {/* Age Range */}
                                <div>
                                  <label className="block text-xs font-medium text-slate-600 mb-1">Age Range</label>
                                  <input
                                    type="text"
                                    value={eventFormData.ageRange}
                                    onChange={(e) => setEventFormData(prev => ({ ...prev, ageRange: e.target.value }))}
                                    placeholder="e.g., All ages"
                                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-azure-400"
                                  />
                                </div>
                                
                                {/* Special Requirements */}
                                <div>
                                  <label className="block text-xs font-medium text-slate-600 mb-1">Requirements</label>
                                  <input
                                    type="text"
                                    value={eventFormData.specialRequirements}
                                    onChange={(e) => setEventFormData(prev => ({ ...prev, specialRequirements: e.target.value }))}
                                    placeholder="e.g., Reservations"
                                    className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-azure-400"
                                  />
                                </div>
                              </div>
                              
                              {/* Pricing Notes / Menu (full width) */}
                              <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">Pricing Notes / Menu</label>
                                <textarea
                                  value={eventFormData.priceNotes}
                                  onChange={(e) => setEventFormData(prev => ({ ...prev, priceNotes: e.target.value }))}
                                  placeholder="e.g., Kids eat free on Sundays, Happy hour 3-6pm"
                                  rows={2}
                                  className="w-full px-3 py-2 border border-stone-200 rounded-lg text-sm focus:outline-none focus:border-azure-400 resize-none"
                                />
                              </div>
                            </div>

                            {/* Submit Button */}
                            <button 
                              onClick={handleFullEventSave}
                              disabled={!inputValue.trim() || isProcessing}
                              className="w-full px-4 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isProcessing ? 'Adding...' : 'Add Family Outing Idea'}
                            </button>
                          </div>
                        )}

                        {/* Scheduled Event Form */}
                        {selectedEventType === 'scheduled-event' && (
                          <div className="space-y-3">
                            {/* Date & Time with icon */}
                            <div className="flex items-center gap-2 p-3 bg-azure-50 border border-azure-200 rounded-xl">
                              <CalendarDays className="w-5 h-5 text-azure-600" />
                              <div className="flex-1">
                                <span className="text-sm font-medium text-slate-900">
                                  {selectedDate ? 
                                    new Date(selectedDate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : 
                                    'Select date'
                                  }
                                </span>
                                {selectedTime && (
                                  <span className="text-slate-600 text-sm ml-2">
                                    at {new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString('en-US', { 
                                      hour: 'numeric', 
                                      minute: '2-digit',
                                      hour12: true 
                                    })}
                                  </span>
                                )}
                              </div>
                              <button 
                                onClick={() => setShowDatePicker(true)}
                                className="p-2 text-azure-600 hover:bg-azure-100 rounded-lg transition-colors"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                            </div>

                            {/* Who's involved - compact */}
                            <div>
                              <label className="block text-xs font-medium text-slate-600 mb-2">Who's involved?</label>
                              <div className="flex gap-2 flex-wrap">
                                {allRecipientOptions.map((member) => {
                                  const isSelected = eventFormData.whoInvolved.includes(member.id);
                                  return (
                                    <button
                                      key={member.id}
                                      onClick={() => {
                                        setEventFormData(prev => ({
                                          ...prev,
                                          whoInvolved: isSelected
                                            ? prev.whoInvolved.filter(id => id !== member.id)
                                            : [...prev.whoInvolved, member.id]
                                        }));
                                      }}
                                      className={`relative w-10 h-10 rounded-full transition-all ${
                                        isSelected
                                          ? 'ring-2 ring-azure-400 ring-opacity-50 shadow-md'
                                          : 'ring-2 ring-transparent hover:ring-stone-300'
                                      }`}
                                    >
                                      {member.role === 'self' ? (
                                        <AvatarOrInitial
                                          avatar={currentUser?.avatar}
                                          name={currentUser?.name || 'You'}
                                          size="lg"
                                        />
                                      ) : (
                                        <AvatarOrInitial
                                          avatar={member.avatar}
                                          name={member.name}
                                          size="lg"
                                        />
                                      )}
                                      {isSelected && (
                                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-azure-400 rounded-full flex items-center justify-center border border-white">
                                          <Check className="w-2.5 h-2.5 text-white" />
                                        </div>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <button 
                              onClick={handleFullEventSave}
                              disabled={!inputValue.trim() || isProcessing}
                              className="w-full px-4 py-3 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors font-medium text-sm mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isProcessing ? 'Adding...' : 'Add Scheduled Event'}
                            </button>
                          </div>
                        )}

                        {/* Personal Event Form */}
                        {selectedEventType === 'personal-event' && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Date & Time</label>
                              <input
                                type="datetime-local"
                                value={eventFormData.dateTime}
                                onChange={(e) => setEventFormData(prev => ({ ...prev, dateTime: e.target.value }))}
                                className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:border-azure-400"
                              />
                            </div>

                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="personalMultiDay"
                                checked={eventFormData.isMultiDay}
                                onChange={(e) => setEventFormData(prev => ({ ...prev, isMultiDay: e.target.checked }))}
                                className="rounded"
                              />
                              <label htmlFor="personalMultiDay" className="text-sm text-slate-700">Multi-day event</label>
                            </div>

                            {eventFormData.isMultiDay && (
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">End Date & Time</label>
                                <input
                                  type="datetime-local"
                                  value={eventFormData.endDateTime}
                                  onChange={(e) => setEventFormData(prev => ({ ...prev, endDateTime: e.target.value }))}
                                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:border-azure-400"
                                />
                              </div>
                            )}

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Privacy</label>
                              <select
                                value={eventFormData.privacy}
                                onChange={(e) => setEventFormData(prev => ({ ...prev, privacy: e.target.value }))}
                                className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:border-azure-400"
                              >
                                <option value="only-me">Only me</option>
                                <option value="adults-only">Adults only</option>
                                <option value="family-visible">Visible to family</option>
                              </select>
                            </div>

                            <button 
                              onClick={handleFullEventSave}
                              disabled={!inputValue.trim() || isProcessing}
                              className="w-full px-4 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors font-medium text-sm mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isProcessing ? 'Adding...' : 'Add Personal Event'}
                            </button>
                          </div>
                        )}

                        {/* Other Item Form */}
                        {selectedEventType === 'other-item' && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Type</label>
                              <select
                                value={eventFormData.itemType}
                                onChange={(e) => setEventFormData(prev => ({ ...prev, itemType: e.target.value }))}
                                className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:border-azure-400"
                              >
                                <option value="general-reminder">General Reminder</option>
                                <option value="birthday">Birthday</option>
                                <option value="anniversary">Anniversary</option>
                                <option value="holiday">Holiday</option>
                                <option value="seasonal-preparation">Seasonal Preparation</option>
                                <option value="maintenance-routine">Maintenance Routine</option>
                                <option value="important-deadline">Important Deadline</option>
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">Date (optional)</label>
                              <input
                                type="date"
                                value={eventFormData.optionalDate}
                                onChange={(e) => setEventFormData(prev => ({ ...prev, optionalDate: e.target.value }))}
                                className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:border-azure-400"
                              />
                            </div>

                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="otherMultiDay"
                                checked={eventFormData.isOtherMultiDay}
                                onChange={(e) => setEventFormData(prev => ({ ...prev, isOtherMultiDay: e.target.checked }))}
                                className="rounded"
                              />
                              <label htmlFor="otherMultiDay" className="text-sm text-slate-700">Multi-day item</label>
                            </div>

                            {eventFormData.isOtherMultiDay && (
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-2">End Date</label>
                                <input
                                  type="date"
                                  value={eventFormData.optionalEndDate}
                                  onChange={(e) => setEventFormData(prev => ({ ...prev, optionalEndDate: e.target.value }))}
                                  className="w-full px-3 py-2 border border-stone-200 rounded-lg focus:outline-none focus:border-azure-400"
                                />
                              </div>
                            )}

                            <button 
                              onClick={handleFullEventSave}
                              disabled={!inputValue.trim() || isProcessing}
                              className="w-full px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium text-sm mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {isProcessing ? 'Adding...' : 'Add Other Item'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Beautiful Calendar Picker - Slides in from bottom */}
      {showDatePicker && (
        <div 
          className="fixed inset-0 z-[60] flex items-end"
          onClick={() => setShowDatePicker(false)}
        >
          {/* Light overlay */}
          <div className="absolute inset-0 bg-black bg-opacity-20" />
          
          {/* Calendar Container */}
          <div 
            className="relative w-full bg-white rounded-t-2xl shadow-2xl max-w-lg mx-auto"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: 'slideInFromBottom 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards',
              maxHeight: '80vh'
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-stone-100">
              <button
                onClick={() => setShowDatePicker(false)}
                className="px-4 py-2 text-slate-600 hover:text-slate-800 font-medium"
              >
                Cancel
              </button>
              
              <div className="flex items-center gap-4">
                <button className="text-slate-600 hover:text-slate-800">Date</button>
                <button className="text-stone-400 hover:text-slate-600">Duration</button>
              </div>
              
              <button
                onClick={() => {
                  setShowDatePicker(false);
                  // Apply the selected date
                }}
                className="px-4 py-2 text-azure-600 hover:text-azure-700 font-medium"
              >
                Done
              </button>
            </div>
            
            {/* Calendar Grid */}
            <div className="p-4">
              {/* Month Navigation */}
              <div className="flex items-center justify-between mb-4">
                <button className="p-1 hover:bg-stone-100 rounded">
                  <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                
                <h3 className="text-lg font-semibold text-slate-900">
                  {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </h3>
                
                <button className="p-1 hover:bg-stone-100 rounded">
                  <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
              
              {/* Weekday Headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
                  <div key={i} className="text-center text-xs font-medium text-stone-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-1">
                {(() => {
                  const today = new Date();
                  const currentYear = today.getFullYear();
                  const currentMonth = today.getMonth();
                  
                  // Get first day of current month and how many days it has
                  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
                  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
                  const daysInMonth = lastDayOfMonth.getDate();
                  const startingDayOfWeek = firstDayOfMonth.getDay(); // 0 = Sunday, 1 = Monday, etc.
                  
                  // Generate calendar grid (6 weeks = 42 days)
                  const calendarDays = [];
                  
                  // Previous month's trailing days
                  const prevMonth = new Date(currentYear, currentMonth - 1, 0);
                  const daysInPrevMonth = prevMonth.getDate();
                  for (let i = startingDayOfWeek - 1; i >= 0; i--) {
                    calendarDays.push({
                      day: daysInPrevMonth - i,
                      isCurrentMonth: false,
                      isPrevMonth: true,
                      date: new Date(currentYear, currentMonth - 1, daysInPrevMonth - i)
                    });
                  }
                  
                  // Current month's days
                  for (let day = 1; day <= daysInMonth; day++) {
                    calendarDays.push({
                      day,
                      isCurrentMonth: true,
                      isPrevMonth: false,
                      date: new Date(currentYear, currentMonth, day)
                    });
                  }
                  
                  // Next month's leading days to fill the grid
                  const remainingSlots = 42 - calendarDays.length;
                  for (let day = 1; day <= remainingSlots; day++) {
                    calendarDays.push({
                      day,
                      isCurrentMonth: false,
                      isPrevMonth: false,
                      date: new Date(currentYear, currentMonth + 1, day)
                    });
                  }
                  
                  return calendarDays.slice(0, 35).map((dayData, i) => {
                    const isToday = dayData.date.toDateString() === today.toDateString();
                    const isSelected = selectedDate && dayData.date.toISOString().split('T')[0] === selectedDate;
                    
                    return (
                      <button
                        key={i}
                        onClick={() => {
                          const dateString = dayData.date.toISOString().split('T')[0];
                          setSelectedDate(dateString);
                        }}
                        className={`
                          aspect-square flex items-center justify-center rounded-lg text-sm font-medium
                          transition-all duration-200
                          ${!dayData.isCurrentMonth ? 'text-stone-300 hover:bg-stone-50' : ''}
                          ${dayData.isCurrentMonth && !isSelected && !isToday ? 'text-slate-700 hover:bg-stone-100' : ''}
                          ${isToday && !isSelected ? 'bg-azure-50 text-azure-600' : ''}
                          ${isSelected ? 'bg-slate-900 text-white' : ''}
                        `}
                      >
                        {dayData.day}
                      </button>
                    );
                  });
                })()}
              </div>
              
              {/* Time Selection */}
              <div className="mt-6 pt-4 border-t border-stone-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium text-slate-700">Time</span>
                  </div>
                  
                  <button 
                    onClick={() => setSelectedTime('')}
                    className="text-sm text-stone-500 hover:text-slate-700"
                  >
                    {selectedTime ? 
                      new Date(`2000-01-01T${selectedTime}`).toLocaleTimeString('en-US', { 
                        hour: 'numeric', 
                        minute: '2-digit',
                        hour12: true 
                      }) : 'None'
                    }
                    <svg className="w-4 h-4 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Calendar & Time Picker */}
      {showDatePicker && (
        <EnhancedDateTimePicker 
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          selectedTime={selectedTime}
          setSelectedTime={setSelectedTime}
          setShowDatePicker={setShowDatePicker}
          eventFormData={eventFormData}
          setEventFormData={setEventFormData}
          dateToYYYYMMDD={dateToYYYYMMDD}
        />
      )}

        {/* Processing Overlay */}
        {isProcessing && (
          <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-[70]">
            <div className="bg-white rounded-lg p-4 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-500"></div>
                <span className="text-sm font-medium text-slate-900">Processing...</span>
              </div>
            </div>
          </div>
        )}
    </>
  );
}